/* eslint-disable jsx-a11y/no-autofocus -- autoFocus nos campos de login é UX
   standard (cursor já no input). Avaliado: leitores de tela anunciam o campo
   focado, não há perda de a11y aqui. */
/**
 * LoginFlow — fluxo de autenticação (V3, polido pra fase de testes).
 *
 * Telas:
 *   `access`            → CTA grande "Sou formando" + CTA secundário "Sou empresa"
 *   `student_sixpass`   → digita sixpass + data de nascimento
 *   `student_password`  → digita senha (caso já cadastrado) + "Esqueci minha senha"
 *   `student_register`  → preenche nome/email/senha (caso primeiro acesso)
 *   `company_login`     → email + senha (parceiros)
 *   `success`           → checkmark animado com mensagem personalizada
 *
 * Decisões pra simplificar:
 *   - Sem phone/SMS (usuário não usa)
 *   - Sem reCAPTCHA (App Check não configurado)
 *   - Validação inicial de elegibilidade via RPC `lookup_student_status`/
 *     `verify_student_eligibility` (chamável por anon — não vaza listagem).
 *   - Cadastro chama `claim_student` (com profile inline) por baixo.
 *   - Admin entra via Google OAuth (regra: email @sixformaturas.com.br).
 *   - "Esqueci minha senha" usa `lookup_student_auth_email` + `resetUserPassword`.
 *
 * UX:
 *   - Erros de validação síncrona → tarjinha vermelha inline (campo vazio, etc).
 *   - Erros assíncronos (RPC, auth, network) → toast (`useToast`).
 *   - Transição de step → fade+slide sutil (180ms) com motion/react.
 *   - Loading → barra fina indeterminada no topo + inputs opaco/disabled.
 *   - Success → checkmark spring + nome do aluno personalizado.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronLeft,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  User,
} from 'lucide-react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { LegalLinks } from '../components/LegalLinks';
import { trackEvent } from '../utils/analytics';
import type { AppSettings } from '../services/types';

interface LoginFlowProps {
  onLoginSuccess: (tab?: string) => void;
  onNavigateToPartnerJoin: () => void;
  appSettings?: AppSettings;
}

type Step =
  | 'access'
  | 'student_sixpass'
  | 'student_password'
  | 'student_register'
  | 'company_login'
  | 'success';

type SuccessKind = 'register' | 'login' | 'company';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PASSWORD_MIN_LEN = 8;
const SUPPORT_WHATSAPP_URL =
  'https://wa.me/5551986430383?text=T%C3%B4%20com%20problema%20pra%20entrar%20no%20Six%20Pass';

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidPassword(value: string) {
  return value.length >= PASSWORD_MIN_LEN;
}

/**
 * Valida data DD/MM/AAAA como data REAL (não só formato).
 * Rejeita: 00/00/0000, 32/13/2099, 29/02 em ano não-bissexto, datas futuras
 * absurdas (>= ano atual), datas muito antigas (< 1900).
 */
function isValidBirthDate(formatted: string): boolean {
  const match = formatted.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (year < 1900 || year > new Date().getFullYear()) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  // Date construtor "estoura" para o mês seguinte se day inválido —
  // ex: new Date(2024, 1, 30) vira 2024-03-01. Comparamos os valores
  // reconstruídos pra detectar.
  const d = new Date(year, month - 1, day);
  return (
    d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
  );
}

function formatBirthDate(raw: string) {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length >= 5) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

function extractErrorMessage(err: unknown): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object') {
    const e = err as Record<string, unknown>;
    // PostgrestError/AuthError do Supabase trazem message + às vezes hint/details
    if (typeof e.message === 'string') return e.message;
    if (typeof e.error_description === 'string') return e.error_description as string;
    if (typeof e.msg === 'string') return e.msg as string;
    try {
      return JSON.stringify(err);
    } catch {
      return '';
    }
  }
  return '';
}

function friendlyError(err: unknown, fallback: string): string {
  // Log sempre o objeto cru pra debug — `console.error('label', err)`
  // (sem [object Object]).
  console.error('[LoginFlow]', err);
  if (!err) return fallback;
  const msg = extractErrorMessage(err);
  const lower = msg.toLowerCase();
  if (lower.includes('invalid login credentials')) return 'Senha incorreta. Tente novamente.';
  if (lower.includes('email not confirmed'))
    return 'Email ainda não confirmado. Cheque sua caixa de entrada.';
  if (lower.includes('user already registered') || lower.includes('already registered'))
    return 'Este email já está cadastrado. Tente fazer login.';
  if (lower.includes('weak password') || lower.includes('password should be at least'))
    return 'Senha fraca. Use ao menos 8 caracteres.';
  if (lower.includes('rate limit') || lower.includes('email rate limit'))
    return 'Muitas tentativas. Espera 1 min e tenta de novo.';
  if (lower.includes('row-level security') || lower.includes('rls'))
    return 'Erro de permissão. Faça logout e entre novamente.';
  if (lower.includes('duplicate key') || lower.includes('already exists'))
    return 'Esse cadastro já existe. Tente fazer login.';
  if (msg.length > 0 && msg.length < 200) return msg;
  return fallback;
}

// ---------------------------------------------------------------------------
// Shell genérico (header + animações + footer)
// ---------------------------------------------------------------------------

interface StepShellProps {
  stepKey: string;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  error?: string;
  loading?: boolean;
  children: React.ReactNode;
}

const StepShell: React.FC<StepShellProps> = ({
  stepKey,
  title,
  subtitle,
  onBack,
  error,
  loading,
  children,
}) => {
  const reducedMotion = useReducedMotion();

  return (
    <div className="relative min-h-[100svh] bg-[#030308] text-white flex flex-col items-center overflow-hidden">
      {/* Ambient glow — copia o padrão do App.tsx */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[50%] rounded-full bg-pink-600/10 blur-[100px] mix-blend-screen" />
        <div className="absolute top-[20%] -right-[20%] w-[60%] h-[60%] rounded-full bg-blue-600/10 blur-[100px] mix-blend-screen" />
        <div className="absolute -bottom-[10%] left-[10%] w-[80%] h-[40%] rounded-full bg-purple-600/10 blur-[100px] mix-blend-screen" />
      </div>

      {/* Barra fina indeterminada de progresso quando loading */}
      <AnimatePresence>
        {loading && (
          <motion.div
            key="progress-track"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-0 left-0 right-0 h-0.5 overflow-hidden z-50"
          >
            <motion.div
              className="h-full w-1/3 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full"
              animate={reducedMotion ? {} : { x: ['-100%', '400%'] }}
              transition={{ duration: 1.2, ease: 'linear', repeat: Infinity }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative w-full max-w-md min-h-[100svh] flex flex-col px-5 sm:px-6 z-10">
        <header
          className="flex shrink-0 items-center justify-between pt-8 pb-4"
          style={{ paddingTop: 'max(2rem, calc(var(--safe-top, 0px) + 1rem))' }}
        >
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="press-feedback flex items-center gap-1 text-sm text-slate-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={18} /> Voltar
            </button>
          ) : (
            <span className="w-8" />
          )}
          <img src="/six-logo.png" alt="Six Formaturas" className="h-9 w-auto object-contain" />
          <span className="w-8" />
        </header>

        <AnimatePresence mode="wait">
          <motion.div
            key={stepKey}
            initial={reducedMotion ? false : { opacity: 0, x: 16 }}
            animate={reducedMotion ? {} : { opacity: 1, x: 0 }}
            exit={reducedMotion ? {} : { opacity: 0, x: -16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="flex-1 flex flex-col justify-center pb-8"
          >
            <h1 className="text-2xl sm:text-3xl font-black mb-2">{title}</h1>
            {subtitle && <p className="text-slate-400 text-sm mb-6">{subtitle}</p>}

            {error && (
              <div
                role="alert"
                className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
              >
                {error}
              </div>
            )}

            {children}
          </motion.div>
        </AnimatePresence>

        <footer className="shrink-0 pb-6 space-y-3">
          <LegalLinks />
          <a
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-300 transition-colors"
          >
            <MessageCircle size={12} aria-hidden="true" />
            Travou? Fala com a gente
          </a>
        </footer>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LoginFlow
// ---------------------------------------------------------------------------

export const LoginFlow: React.FC<LoginFlowProps> = ({
  onLoginSuccess,
  onNavigateToPartnerJoin,
  appSettings: _appSettings,
}) => {
  const [step, setStep] = useState<Step>('access');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successKind, setSuccessKind] = useState<SuccessKind>('login');

  // Form state — compartilhado entre vários steps.
  const [sixpassNumber, setSixpassNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const {
    loginStudent,
    registerStudent,
    loginCompany,
    signInWithGoogle,
    resetUserPassword,
    profile,
  } = useAuth();
  const { showToast } = useToast();
  const reducedMotion = useReducedMotion();
  const adminLockRef = useRef(false);

  // Helper pra erros assíncronos (rede / RPC / auth) — vai pro toast.
  const showAsyncError = (msg: string) => showToast(msg, 'error');

  const goBack = (target: Step = 'access') => {
    setError('');
    setStep(target);
  };

  // ---------------------- Step 1: Sixpass ----------------------
  const handleSixpassSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const sixpass = sixpassNumber.trim();
    if (!sixpass) {
      setError('Digite seu número Six Pass.');
      return;
    }
    if (birthDate.length !== 10) {
      setError('Digite a data de nascimento completa (DD/MM/AAAA).');
      return;
    }
    // Validação semântica: 00/00/0000 ou 32/13/2099 passavam o check de
    // length, geravam RPC "not_found" e mensagem confusa pro user.
    if (!isValidBirthDate(birthDate)) {
      setError('Data de nascimento inválida. Confere se tá certo.');
      return;
    }

    setLoading(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('verify_student_eligibility', {
        p_sixpass_number: sixpass,
        p_birth_date: birthDate,
      });
      if (rpcErr) throw rpcErr;
      switch (data) {
        case 'not_found':
          showAsyncError(
            'Não encontramos esse Six Pass com essa data de nascimento. Verifique e tente novamente.',
          );
          return;
        case 'inactive':
          showAsyncError('Este Six Pass está inativo. Procure a equipe Six.');
          return;
        case 'already_claimed':
          // Já cadastrado → vai pro login com senha.
          setStep('student_password');
          return;
        case 'eligible':
          // Primeiro acesso → cadastro completo.
          setStep('student_register');
          return;
        default:
          showAsyncError('Não conseguimos validar agora. Tente novamente.');
      }
    } catch (err) {
      showAsyncError(friendlyError(err, 'Não conseguimos validar seu Six Pass agora.'));
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- Step 2a: Login (já cadastrado) ----------------------
  const handleStudentLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!password) {
      setError('Digite sua senha.');
      return;
    }

    setLoading(true);
    try {
      await loginStudent(sixpassNumber.trim(), password);
      trackEvent('login_succeeded', { kind: 'student', sixpass: sixpassNumber.trim() });
      setSuccessKind('login');
      setStep('success');
      window.setTimeout(() => onLoginSuccess(), 600);
    } catch (err) {
      showAsyncError(friendlyError(err, 'Não conseguimos entrar. Verifique sua senha.'));
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- Esqueci minha senha ----------------------
  const handleForgotPassword = async () => {
    if (loading) return;
    const sixpass = sixpassNumber.trim();
    if (!sixpass) {
      setError('Volta um passo e digita seu Six Pass primeiro.');
      return;
    }

    setLoading(true);
    try {
      // Descobre o email cadastrado a partir do número Six Pass.
      const { data, error: rpcErr } = await supabase.rpc('lookup_student_auth_email', {
        p_sixpass_number: sixpass,
      });
      if (rpcErr) throw rpcErr;
      if (!data || typeof data !== 'string') {
        showAsyncError(
          'Não encontramos um e-mail vinculado a este Six Pass. Fala com a equipe Six.',
        );
        return;
      }
      await resetUserPassword(data);
      showToast(
        'Enviamos um link de redefinição pro seu e-mail cadastrado. Confere a caixa de entrada (e o spam).',
        'success',
      );
    } catch (err) {
      showAsyncError(
        friendlyError(err, 'Não conseguimos enviar o link agora. Tente novamente em 1 minuto.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- Step 2b: Cadastro (novo) ----------------------
  const handleStudentRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Digite seu nome.');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Digite um e-mail válido.');
      return;
    }
    if (!isValidPassword(password)) {
      setError(`Senha precisa ter pelo menos ${PASSWORD_MIN_LEN} caracteres.`);
      return;
    }
    if (password !== passwordConfirm) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      // gender e cpf opcionais — a app pede depois no Profile.
      await registerStudent(
        sixpassNumber.trim(),
        birthDate,
        email.trim().toLowerCase(),
        '',
        password,
        'other',
        name.trim(),
        '',
      );
      trackEvent('register_succeeded', { sixpass: sixpassNumber.trim() });
      setSuccessKind('register');
      setStep('success');
      window.setTimeout(() => onLoginSuccess(), 600);
    } catch (err) {
      showAsyncError(friendlyError(err, 'Não conseguimos criar sua conta agora. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- Empresa ----------------------
  const handleCompanyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setError('Digite um e-mail válido.');
      return;
    }
    if (!password) {
      setError('Digite a senha.');
      return;
    }

    setLoading(true);
    try {
      await loginCompany(email.trim().toLowerCase(), password);
      setSuccessKind('company');
      setStep('success');
      window.setTimeout(() => onLoginSuccess('partner-dashboard'), 600);
    } catch (err) {
      showAsyncError(friendlyError(err, 'Email ou senha incorretos.'));
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- Admin ----------------------
  const handleAdminLogin = async () => {
    if (adminLockRef.current) return;
    adminLockRef.current = true;
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      // Redirect feito pelo Supabase (volta na URL com hash de session).
      // Se o redirect não acontecer (ex: popup blocker), libera o lock
      // depois de 5s pra user poder tentar de novo. Sem isso, o botão
      // ficava permanente bloqueado.
      window.setTimeout(() => {
        adminLockRef.current = false;
      }, 5000);
    } catch (err) {
      adminLockRef.current = false;
      showAsyncError(friendlyError(err, 'Não conseguimos fazer login Google agora.'));
    } finally {
      setLoading(false);
    }
  };

  // Mostra mensagem de erro vinda de redirect do Google (admin não autorizado).
  useEffect(() => {
    try {
      const stored = window.sessionStorage?.getItem('sixpass:auth-error');
      if (stored) {
        showToast(stored, 'error');
        window.sessionStorage.removeItem('sixpass:auth-error');
      }
    } catch {
      /* sessionStorage indisponível em modo privado */
    }
  }, [showToast]);

  // ---------------------- Renders ----------------------

  if (step === 'access') {
    return (
      <StepShell
        stepKey="access"
        title="Pronto pra começar?"
        subtitle="Entre no app oficial do Six Pass e destrave seus benefícios em parceiros locais."
        error={error}
        loading={loading}
      >
        <div className="flex flex-col gap-3">
          {/* CTA principal: aluno */}
          <button
            type="button"
            onClick={() => {
              setError('');
              setSixpassNumber('');
              setBirthDate('');
              setStep('student_sixpass');
            }}
            disabled={loading}
            className="group w-full rounded-3xl border border-white/10 bg-gradient-to-br from-pink-500/20 via-blue-500/15 to-blue-900/10 p-6 sm:p-7 text-left transition-ui hover:border-white/20 hover:shadow-xl hover:shadow-pink-500/10 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="flex items-start gap-4 mb-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-blue-500 shadow-lg shadow-pink-500/30">
                <User size={26} className="text-white" />
              </div>
              <div className="flex-1 mt-1">
                <p className="font-black text-xl text-white">Sou formando</p>
                <p className="text-xs text-slate-300 mt-0.5">
                  Entrar com meu Six Pass
                </p>
              </div>
              <motion.div
                animate={reducedMotion ? {} : { x: [0, 4, 0] }}
                transition={{ duration: 1.8, ease: 'easeInOut', repeat: Infinity }}
                className="mt-2"
              >
                <ArrowRight size={22} className="text-white" />
              </motion.div>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-300/80">
              <Sparkles size={12} aria-hidden="true" />
              <span>Descontos em restaurantes, beleza, eventos e mais</span>
            </div>
          </button>

          {/* CTA secundário: empresa */}
          <button
            type="button"
            onClick={() => {
              setError('');
              setEmail('');
              setPassword('');
              setStep('company_login');
            }}
            disabled={loading}
            className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:bg-white/[0.06] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 text-slate-300">
              <Building2 size={16} />
            </span>
            <div className="flex-1">
              <p className="text-sm font-bold">Sou empresa parceira</p>
              <p className="text-[11px] text-slate-400">Gerenciar benefícios</p>
            </div>
            <ArrowRight size={16} className="text-slate-400" />
          </button>
        </div>

        <button
          type="button"
          onClick={onNavigateToPartnerJoin}
          className="press-feedback mt-6 w-full text-center text-sm text-slate-400 hover:text-white"
        >
          Quer virar parceiro Six?{' '}
          <span className="font-bold underline underline-offset-2">Indique sua empresa</span>
        </button>

        <button
          type="button"
          onClick={handleAdminLogin}
          disabled={loading}
          className="press-feedback mx-auto mt-8 flex items-center gap-2 text-xs text-slate-400 hover:text-slate-300 disabled:opacity-50"
        >
          <Lock size={12} /> Admin
        </button>
      </StepShell>
    );
  }

  if (step === 'student_sixpass') {
    return (
      <StepShell
        stepKey="student_sixpass"
        title="Quase lá ✨"
        subtitle="Digite seu número Six Pass e sua data de nascimento."
        error={error}
        loading={loading}
        onBack={() => goBack('access')}
      >
        <form onSubmit={handleSixpassSubmit} className="space-y-4">
          <FormField label="Número Six Pass">
            <input
              autoFocus
              inputMode="numeric"
              autoComplete="username"
              enterKeyHint="next"
              value={sixpassNumber}
              onChange={(e) => setSixpassNumber(e.target.value)}
              placeholder="Ex: 12345678"
              disabled={loading}
              className={inputClass}
            />
          </FormField>

          <FormField label="Data de nascimento">
            <input
              inputMode="numeric"
              autoComplete="bday"
              enterKeyHint="go"
              value={birthDate}
              onChange={(e) => setBirthDate(formatBirthDate(e.target.value))}
              placeholder="DD/MM/AAAA"
              disabled={loading}
              className={inputClass}
            />
          </FormField>

          <PrimaryButton loading={loading}>Continuar</PrimaryButton>
        </form>
      </StepShell>
    );
  }

  if (step === 'student_password') {
    return (
      <StepShell
        stepKey="student_password"
        title="Olá de novo"
        subtitle={`Six Pass ${sixpassNumber} — entre com sua senha.`}
        error={error}
        loading={loading}
        onBack={() => goBack('student_sixpass')}
      >
        <form onSubmit={handleStudentLogin} className="space-y-4">
          <FormField label="Senha">
            <PasswordInput
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              placeholder="••••••••"
              disabled={loading}
            />
          </FormField>

          <div className="flex">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading}
              className="press-feedback text-sm text-slate-400 transition-colors hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Esqueci minha senha
            </button>
          </div>

          <PrimaryButton loading={loading}>Entrar</PrimaryButton>
        </form>
      </StepShell>
    );
  }

  if (step === 'student_register') {
    return (
      <StepShell
        stepKey="student_register"
        title="Falta pouco"
        subtitle="Preencha pra desbloquear suas vantagens."
        error={error}
        loading={loading}
        onBack={() => goBack('student_sixpass')}
      >
        <form onSubmit={handleStudentRegister} className="space-y-4">
          <FormField label="Nome completo">
            <input
              autoFocus
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
              disabled={loading}
              className={inputClass}
            />
          </FormField>

          <FormField label="E-mail">
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              disabled={loading}
              className={inputClass}
            />
          </FormField>

          <FormField label="Senha (mínimo 8 caracteres)">
            <PasswordInput
              autoComplete="new-password"
              value={password}
              onChange={setPassword}
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              placeholder="••••••••"
              disabled={loading}
            />
          </FormField>

          <FormField label="Confirme a senha">
            <PasswordInput
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              placeholder="••••••••"
              disabled={loading}
            />
          </FormField>

          <PrimaryButton loading={loading}>Criar conta</PrimaryButton>
        </form>
      </StepShell>
    );
  }

  if (step === 'company_login') {
    return (
      <StepShell
        stepKey="company_login"
        title="Acesso parceiro"
        subtitle="Use o e-mail e senha que a equipe Six configurou pra você."
        error={error}
        loading={loading}
        onBack={() => goBack('access')}
      >
        <form onSubmit={handleCompanyLogin} className="space-y-4">
          <FormField label="E-mail">
            <input
              autoFocus
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              disabled={loading}
              className={inputClass}
            />
          </FormField>

          <FormField label="Senha">
            <PasswordInput
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              placeholder="••••••••"
              disabled={loading}
            />
          </FormField>

          <PrimaryButton loading={loading}>Entrar</PrimaryButton>
        </form>
      </StepShell>
    );
  }

  // success — usa o profile carregado se existir (login), senão o name
  // digitado (cadastro). Antes login mostrava só "Bem-vindo de volta"
  // genérico porque `name` do form ficava vazio no fluxo de login.
  const profileFirstName = profile?.name?.trim().split(' ')[0] || '';
  const formFirstName = name.trim().split(' ')[0];
  const successTitle =
    successKind === 'register' && formFirstName
      ? `Bem-vindo, ${formFirstName}!`
      : successKind === 'company'
        ? 'Pronto!'
        : profileFirstName
          ? `Bem-vindo de volta, ${profileFirstName}!`
          : 'Bem-vindo de volta!';

  return (
    <StepShell stepKey="success" title={successTitle}>
      <div className="flex flex-col items-center gap-4 py-12">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 12 }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400"
        >
          <CheckCircle2 size={44} />
        </motion.div>
        <p className="text-slate-300">Carregando suas vantagens…</p>
      </div>
    </StepShell>
  );
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

const inputClass =
  'w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/60 focus:bg-white/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

const FormField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

interface PasswordInputProps {
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
  autoComplete?: string;
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  show,
  onToggleShow,
  autoComplete,
  autoFocus,
  placeholder,
  disabled,
}) => (
  <div className="relative">
    <input
      type={show ? 'text' : 'password'}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={inputClass + ' pr-12'}
    />
    <button
      type="button"
      onClick={onToggleShow}
      tabIndex={-1}
      disabled={disabled}
      className="press-feedback absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 disabled:opacity-40"
      aria-label={show ? 'Esconder senha' : 'Mostrar senha'}
    >
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
);

const PrimaryButton: React.FC<{ children: React.ReactNode; loading?: boolean }> = ({
  children,
  loading,
}) => (
  <button
    type="submit"
    disabled={loading}
    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 to-blue-500 py-3.5 font-bold text-white shadow-lg shadow-blue-500/20 transition-shadow hover:shadow-pink-500/30 disabled:opacity-60 disabled:cursor-not-allowed"
  >
    {loading ? (
      <Loader2 size={20} className="animate-spin" />
    ) : (
      <>
        {children}
        <ShieldCheck size={16} className="opacity-80" />
      </>
    )}
  </button>
);
